import React, { FC } from 'react';
import { useIntlBds } from '../../../../../translations/BdsIntlProvider';
import { dayjs } from '../../../../../utils/dateObject';
import { ChipsSelect } from '../../../../ChipsSelect/ChipsSelect';
import { Switch } from '../../../../Form/Switch';
import { Select } from '../../../../Select/Select';
import { Icon } from '../../../../Icon/Icon';
import { Input } from '../../../../Form/Input';
import { DataTableTypes } from '../../../DataTableTypes';
import { dataTableClassnames } from '../../const/classNames';
import { DatePicker } from '../../../../DatePicker/DatePicker';
import { FormLabel } from '../../../../Form/FormLabel';
import { Grid } from '../../../../Grid/Grid';

interface GetFilterProps {
  filter: DataTableTypes.Filters.FilterDefinition<DataTableTypes.QueryModel>;
  queryModel: DataTableTypes.QueryModel;
  setQueryModel: (key: string, value: unknown | undefined) => void;
  getQueryValueById: (queryModel: DataTableTypes.QueryModel, key: string | string[]) => any;
}

export const FilterElement: FC<GetFilterProps> = ({ filter, queryModel, getQueryValueById, setQueryModel }) => {
  const thisValue = getQueryValueById(queryModel, filter.id) ?? undefined;
  const { intl } = useIntlBds();
  const { id } = filter;
  const dataTestBasedOnId = dataTableClassnames.filters.filter(id);
  const [lastSearchTerm, setLastSearchTerm] = React.useState<Record<string, string>>({});
  const [shouldMakeCallOnOpen, setShouldMakeCallOnOpen] = React.useState<Record<string, boolean | undefined>>({});

  switch (filter.type) {
    case DataTableTypes.Filters.Type.custom: {
      return filter.component;
    }
    case DataTableTypes.Filters.Type.multiSelect: {
      return (
        <Select<unknown, true>
          label={filter.label}
          options={filter.options ?? EMPTY_ARRAY}
          fullWidth
          placeholder={filter.placeholder}
          dataTest={dataTestBasedOnId}
          multiple
          value={(thisValue as unknown as string[]) ?? EMPTY_ARRAY}
          onChange={(values) => {
            setQueryModel(id, values?.length ? values : undefined);
            if (filter.onSearch && filter.options.every((it) => (values ?? []).includes(it.value))) {
              // if all visible options are selected, clear the search
              filter.onSearch('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
            }
          }}
          autoComplete={filter.autoComplete}
          loading={filter.loading}
          onBlur={() => {
            if (filter.autoComplete && !!lastSearchTerm[id]) {
              filter.onSearch?.('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
            }
          }}
          onOpen={() => {
            if (shouldMakeCallOnOpen[id] === true) {
              filter.onSearch?.('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
              setShouldMakeCallOnOpen((prev) => ({ ...prev, [id]: false }));
            }
          }}
          onInit={
            filter.onInit
              ? (uids) => {
                  filter.onInit?.(uids as string[]);
                  setShouldMakeCallOnOpen((prev) => ({ ...prev, [id]: !!uids?.length }));
                }
              : undefined
          }
          onSearch={(search) => {
            setLastSearchTerm((prev) => ({ ...prev, [id]: search }));
            filter.onSearch?.(search);
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.singleSelect: {
      return (
        <Select<unknown, false>
          label={filter.label}
          options={filter.options ?? EMPTY_ARRAY}
          fullWidth
          placeholder={filter.placeholder}
          dataTest={dataTestBasedOnId}
          value={thisValue}
          onChange={(values) => {
            setQueryModel(id, values);
            if (filter.onSearch && filter.options.length === 1 && filter.options[0]?.value === values) {
              // if an option is selected, clear the search
              filter.onSearch('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
            }
          }}
          autoComplete={filter.autoComplete}
          loading={filter.loading}
          onBlur={() => {
            if (filter.autoComplete && !!lastSearchTerm[id]) {
              filter.onSearch?.('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
            }
          }}
          onOpen={() => {
            if (shouldMakeCallOnOpen[id] === true) {
              filter.onSearch?.('');
              setLastSearchTerm((prev) => ({ ...prev, [id]: '' }));
              setShouldMakeCallOnOpen((prev) => ({ ...prev, [id]: false }));
            }
          }}
          onInit={
            filter.onInit
              ? (uid) => {
                  filter.onInit?.(uid as string);
                  setShouldMakeCallOnOpen((prev) => ({ ...prev, [id]: true }));
                }
              : undefined
          }
          onSearch={(search) => {
            setLastSearchTerm((prev) => ({ ...prev, [id]: search }));
            filter.onSearch?.(search);
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.singleChipSelect: {
      return (
        <ChipsSelect<unknown, false>
          label={filter.label}
          options={filter.options ?? EMPTY_ARRAY}
          dataTest={dataTestBasedOnId}
          value={thisValue ?? EMPTY_ARRAY}
          onSelect={(values) => {
            setQueryModel(id, values);
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.multiChipSelect: {
      return (
        <ChipsSelect<unknown, true>
          multiselect
          label={filter.label}
          options={filter.options ?? EMPTY_ARRAY}
          dataTest={dataTestBasedOnId}
          value={thisValue ?? EMPTY_ARRAY}
          onSelect={(values) => {
            setQueryModel(id, values?.length ? values : undefined);
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.switch: {
      return (
        <Switch
          dataTest={dataTestBasedOnId}
          checked={thisValue}
          id={dataTestBasedOnId}
          size="small"
          label={filter.label}
          onChange={() => {
            let val: boolean | undefined = !thisValue;
            if (!val && filter.undefinedOnFalse) {
              val = undefined;
            }
            setQueryModel(id, val);
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.input: {
      return (
        <Input
          label={filter.label}
          key={filter.id}
          fullWidth
          type={filter.inputType}
          placeholder={filter.placeholder}
          dataTest={dataTestBasedOnId}
          startAdornment={filter?.startIcon ? <Icon alt={filter.startIcon} name={filter.startIcon} /> : undefined}
          slotProps={{
            htmlInput: {
              min: filter.numberProps?.min,
              max: filter.numberProps?.max,
              step: filter.numberProps?.step
            }
          }}
          value={
            filter.inputType === 'number'
              ? typeof thisValue === 'number'
                ? thisValue
                : ''
              : (thisValue as string | number) || ''
          }
          onChange={({ target }) => {
            if (filter.inputType === 'number') {
              const val: number | undefined = Number.parseFloat(target.value);
              setQueryModel(id, !Number.isNaN(val) ? val : undefined);
            } else {
              setQueryModel(id, target.value || undefined);
            }
          }}
        />
      );
    }
    case DataTableTypes.Filters.Type.date: {
      return (
        <DatePicker
          value={thisValue ? dayjs(thisValue as string) : null}
          onChange={(date) => {
            if (date) {
              setQueryModel(id, date.utc(true).toDate());
            } else {
              setQueryModel(id, undefined);
            }
          }}
          label={filter.label}
          slotProps={{
            textField: {
              placeholder: filter.placeholder,
              fullWidth: true
            }
          }}
          clearable
          disableFuture={filter.disableFuture}
          disablePast={filter.disablePast}
          dataTest={dataTestBasedOnId}
        />
      );
    }
    case DataTableTypes.Filters.Type.dateRange: {
      const endValueRaw = getQueryValueById(queryModel, filter.endDateKey) ?? undefined;
      const startValue = thisValue ? dayjs(thisValue as string) : null;
      const endValue = endValueRaw ? dayjs(endValueRaw as string) : null;
      return (
        <div>
          <FormLabel htmlFor={id} dataTest={`${dataTestBasedOnId}-label`} shrink sx={{ mb: 2 }}>
            {filter.label}
          </FormLabel>
          <Grid container spacing={3}>
            <Grid size={6}>
              <DatePicker
                value={startValue}
                onChange={(date) => {
                  if (date) {
                    setQueryModel(id, date.utc(true).toDate());
                  } else {
                    setQueryModel(id, undefined);
                  }
                }}
                slotProps={{
                  textField: {
                    id,
                    placeholder: intl.DataTable.Filters.dateRange.from,
                    fullWidth: true
                  }
                }}
                clearable
                maxDate={endValue ? endValue.subtract(1, 'day') : undefined}
                dataTest={`${dataTestBasedOnId}-start`}
              />
            </Grid>
            <Grid size={6}>
              <DatePicker
                value={endValue}
                onChange={(date) => {
                  if (date) {
                    setQueryModel(filter.endDateKey, date.utc(true).toDate());
                  } else {
                    setQueryModel(filter.endDateKey, undefined);
                  }
                }}
                slotProps={{
                  textField: {
                    placeholder: intl.DataTable.Filters.dateRange.to,
                    fullWidth: true
                  }
                }}
                clearable
                minDate={startValue ? startValue.add(1, 'day') : undefined}
                dataTest={`${dataTestBasedOnId}-end`}
              />
            </Grid>
          </Grid>
        </div>
      );
    }
    case DataTableTypes.Filters.Type.hidden: {
      return null;
    }
  }
};

const EMPTY_ARRAY: any[] = [];
